import React, {useRef, useState} from 'react';
import {Input as UInput} from '../../components/input';
import {Form} from '@unform/web';
import * as Yup from 'yup';
import moment from 'moment';
import {Col, FormGroup, Input, Label, Row} from 'reactstrap';
import I18nMessage from '../../components/I18nMessage';
import ReactSelect from '../../components/react-select';
import {TextArea} from '../../components/textarea';
import useAlerts from '../../hooks/use-alerts';
import UInputMask from '../../components/react-mask';
import Button from '../../components/button';
import {useIntl} from 'react-intl';
import useStore from 'src/hooks/use-store.js';

const OccurrencesForm = (props) => {
    const {
        sinister,
        loadIncidents,
        close,
        setSinister,
        setOccurrences,
    } = props;

    const intl = useIntl();
    const {toast} = useAlerts();
    const [loading] = useState(false);
    const [file, setFile] = useState(null);
    const [ext, setExt] = useState(null);
    const formRef = useRef(null);
    const fileRef = useRef(null);
    const {store} = useStore();
    const {sinisterStore} = store;
    const [currentCategory, setCurrentCategory] = useState(null);
    const isPositionUpdateSelected = currentCategory?.id === 'positionUpdate';

    const baseSchema = Yup.object().shape({
        sinisterId: Yup.number().notRequired(),
        category: Yup.string().required(),
        date: Yup.string().required(),
        time: Yup.string().required(),
        address: Yup.string().notRequired(),
        description: Yup.string().required(),
    });

    const positionUpdateSchema = baseSchema.clone().shape({
        latitude: Yup.number().required(),
        longitude: Yup.number().required(),
    });

    const schema = isPositionUpdateSelected ? positionUpdateSchema : baseSchema;

    const handleProgress = (event) => {
        const file = event.currentTarget.files[0];
        setExt(file.name.slice((file.name.lastIndexOf('.') - 1 >>> 0) + 2));
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = function() {
            setFile(reader.result);
        };
        reader.onerror = function(error) {
            console.log('Error: ', error);
        };
    };

    const handleSubmit = async (payload) => {
        try {
            await schema.validate(payload, {
                abortEarly: false,
            });
            payload = schema.cast(payload);

            sinisterStore.applyPositionAndAddOccurrence({
                ...payload,
                ext,
                file,
            }).then((sinister) => {
                loadIncidents();
                setSinister(sinister);
                setOccurrences(sinister.occurrences);
                close();
                toast.success(intl.formatMessage({id: 'sinisterOccurrenceAddedSuccess'}));
                formRef.current.reset();
                formRef.current.setErrors({});
                formRef.current.setFieldValue('sinisterId', sinister.id);
                formRef.current.setFieldValue('date', moment().format(intl.formatMessage({id: 'dateFormat'})));
                formRef.current.setFieldValue('time', moment().format('HH:mm'));
            });
        } catch (err) {
            toast.errorStatus(intl.formatMessage({id: 'sinisterOccurrenceCreateError'}));
            const validationErrors = {};
            if (err instanceof Yup.ValidationError) {
                err.inner.forEach((error) => {
                    validationErrors[error.path] = error.message;
                });
                formRef.current.setErrors(validationErrors);
            }
            props.dispatchAccordionResize();
        }
    };

    const onChangeCategory = (option) => {
        setCurrentCategory(option);
        // needed to ensure it will be schedule to after setState sync
        setTimeout(() => {
            props?.dispatchAccordionResize && props.dispatchAccordionResize();
        }, 0);
    };

    return (
        <Form
            ref={formRef}
            onSubmit={handleSubmit}
            initialData={{
                sinisterId: sinister.id,
                date: moment().format(intl.formatMessage({id: 'dateFormat'})),
                time: moment().format('HH:mm'),
            }}>

            <UInput hidden type="number" name="sinisterId"/>

            <FormGroup className="custom-formGroup">
                <Label for="name">
                    <I18nMessage id="deviceCategory"/>*
                </Label>
                <ReactSelect
                    id={'category'}
                    name={'category'}
                    onChange={onChangeCategory}
                    options={[
                        {id: 'attemptToTriggerPromptResponse', title: 'Tentativa De Acionar A Resposta Imediata'},
                        {id: 'limitExceededInSearchConditions', title: intl.formatMessage({id: 'sinisterTypeLimitExceededInSearchConditions'})},
                        {id: 'policeArriveAtTheScene', title: intl.formatMessage({id: 'sinisterTypePoliceArriveAtTheScene'})},
                        {id: 'promptResponseActivated', title: 'Resposta Ativada'},
                        {id: 'registerThePoliceReport', title: intl.formatMessage({id: 'sinisterTypeRegisterThePoliceReport'})},
                        {
                            id: 'requestedVehicleForVehicleRecovery',
                            title: intl.formatMessage({id: 'sinisterTypeRequestedVehicleForVehicleRecovery'}),
                        },
                        {id: 'supportArrivesOnSite', title: ' Suporte Chega Ao Local'},
                        {id: 'supportDoesOnSitePreservation', title: intl.formatMessage({id: 'sinisterTypeSupportDoesOnSitePreservation'})},
                        {id: 'supportOnTheWay', title: 'Apoio No Caminho'},
                        {id: 'supportStartsPatrolling', title: intl.formatMessage({id: 'sinisterTypeSupportStartsPatrolling'})},
                        {id: 'uninstalledEquipmentFound', title: 'Equipamento Desinstalado Encontrado'},
                        {id: 'positionUpdate', title: intl.formatMessage({id: 'sinisterTypePositionUpdate'})},
                    ]}/>
            </FormGroup>
            <Row>
                <Col>
                    <FormGroup className="custom-formGroup">
                        <Label for="name">
                            Data*
                        </Label>
                        <UInputMask name={'date'} mask={'99/99/9999'}/>
                    </FormGroup>
                </Col>
                <Col>
                    <FormGroup className="custom-formGroup">
                        <Label for="name">{intl.formatMessage({id: 'sinisterTimeField'})}</Label>
                        <UInputMask name={'time'} mask={'99:99'}/>
                    </FormGroup>
                </Col>
            </Row>
            <FormGroup className="custom-formGroup">
                <Label for="name">
                    <I18nMessage id="sharedAddress"/>
                </Label>
                <Input
                    tag={UInput}
                    type="text"
                    name={'address'}
                    id="address"
                />
            </FormGroup>
            {isPositionUpdateSelected && (
                <Row>
                    <Col>
                        <FormGroup className="custom-formGroup">
                            <Label><I18nMessage id="positionLatitude" />*</Label>
                            <Input
                                plaintext
                                type="text"
                                inputMode='numeric'
                                pattern='\d*'
                                defaultValue={0}
                                tag={UInput}
                                id="latitude"
                                name="latitude"
                            />
                        </FormGroup>
                    </Col>
                    <Col>
                        <FormGroup className="custom-formGroup">
                            <Label><I18nMessage id="positionLongitude" />*</Label>
                            <Input
                                plaintext
                                type="text"
                                inputMode='numeric'
                                pattern='\d*'
                                defaultValue={0}
                                tag={UInput}
                                id="longitude"
                                name="longitude"
                            />
                        </FormGroup>
                    </Col>
                </Row>
            )}
            <FormGroup className="custom-formGroup">
                <Label for="name">{intl.formatMessage({id: 'sinisterAddImage'})}</Label>
                <Button className={'outline'}
                        size={'sm'}
                        color={'theme'} onClick={ () => fileRef.current.click()}><i className="fas fa-arrow-up"/> Upload</Button>
                <input
                    style={{display: 'none'}}
                    type="file"
                    ref={fileRef}
                    onChange={handleProgress}
                />
            </FormGroup>
            <FormGroup className="custom-formGroup">
                <Label for="name">
                    <I18nMessage id="sharedDescription"/>
                </Label>
                <TextArea
                    type="text"
                    name={'description'}
                    id="description"
                />
            </FormGroup>

            <Row>
                <Col> <Button
                    color={'warning'}
                    disabled={loading}
                    className={'outline'}
                    size={'sm'}
                    onClick={() => close()}>
                    {intl.formatMessage({id: 'sharedCancel'})}
                </Button></Col>
                <Col>
                    <Button
                        color={'success'}
                        disabled={loading}
                        className={'outline'}
                        size={'sm'}
                        onClick={() => formRef.current.submitForm()}>
                        {intl.formatMessage({id: 'sharedSave'})}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default OccurrencesForm;
